import { registerQueryCallback } from './queryCallbackRegistry'

import {
  AuthError,
  consumeStoredPostLoginRedirect,
  toPostLoginLocation,
  useSession,
} from '@/features/auth'
import { useToast } from '@/shared/ui'

export interface AuthQueryCallbackDeps {
  exchange?: ReturnType<typeof useSession>['exchangeTokenHandoff']
  takeTarget?: () => string | null
  reportError?: (key: string) => void
}

/** App-side composition only: auth owns the exchange; shell owns callback registration/redirect. */
export function registerAuthQueryCallback(
  /** A key resolver — `i18n.global.t` narrowed to the single-argument shape actually used. */
  t: (key: string) => string,
  deps: AuthQueryCallbackDeps = {},
): void {
  const exchange = deps.exchange ?? useSession().exchangeTokenHandoff
  const reportError = deps.reportError ?? ((key: string) => useToast().error(t(key)))

  registerQueryCallback('handoff', async ({ value }) => {
    try {
      // Capture the redirect before exchanging the one-time handoff. Consumers commonly clear
      // their pending target as part of the exchange callback, so reading it afterwards loses the
      // destination across the auth leg.
      const target = deps.takeTarget?.() ?? consumeStoredPostLoginRedirect()
      await exchange(value)
      return target ? { redirect: toPostLoginLocation(target) } : undefined
    } catch (error) {
      reportError(AuthError.from(error).i18nKey)
      return
    }
  })
}
